import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { isAuthenticated, markPostLoginFeaturePromptPending, setAuth } from '../services/auth';
import { Eye, EyeOff, Landmark, Smartphone, Keyboard } from 'lucide-react';
import { toast } from 'sonner';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { ThemeToggle } from '../components/ThemeToggle';
import { ImageCarousel } from '../components/ImageCarousel';
import { SecurityManager } from '../components/SecurityManager';
import VirtualKeyboard from '../components/VirtualKeyboard';
import { getDefaultAuthenticatedPath } from '../config/navigation';
import { useLocalization } from '../context/LocalizationContext';
import { resolveApiErrorMessage } from '../utils/apiErrors';

const OTP_RESEND_COOLDOWN_SECONDS = 30;
const OTP_RESEND_MAX_ATTEMPTS = 3;
const INDIA_MOBILE_PATTERN = /^[6-9]\d{9}$/;
const USERNAME_MAX_LENGTH = 50;
const PASSWORD_MAX_LENGTH = 128;
const OTP_MAX_LENGTH = 6;

const sanitizeUsernameInput = (value) =>
  String(value || '')
    .replace(/\s+/g, '')
    .replace(/[^a-zA-Z0-9@._-]/g, '')
    .slice(0, USERNAME_MAX_LENGTH);

const sanitizePasswordInput = (value) =>
  String(value || '')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .slice(0, PASSWORD_MAX_LENGTH);

const sanitizeIndianMobileInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  let normalized = '';

  for (const digit of digits) {
    if (!normalized && !/[6-9]/.test(digit)) {
      continue;
    }
    if (normalized.length >= 10) {
      break;
    }
    normalized += digit;
  }

  return normalized;
};

const sanitizeOtpInput = (value) => String(value || '').replace(/\D/g, '').slice(0, OTP_MAX_LENGTH);

const maskMobileNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (INDIA_MOBILE_PATTERN.test(digits)) {
    return `${digits.slice(0, 2)}XXXXX${digits.slice(-3)}`;
  }
  if (digits.length <= 5) return `${'*'.repeat(digits.length)}`;
  if (digits.length <= 7) {
    return `${digits.slice(0, 2)}${'*'.repeat(digits.length - 2)}`;
  }
  return `${digits.slice(0, 2)}${'*'.repeat(Math.max(0, digits.length - 5))}${digits.slice(-3)}`;
};

const normalizeAuthPayload = (payload, fallback = {}) => {
  const root = payload?.value && typeof payload.value === 'object' ? payload.value : payload || {};
  const nestedUser = root.user && typeof root.user === 'object' ? root.user : {};
  const name =
    root.memberName ||
    root.customerName ||
    root.agentName ||
    root.name ||
    nestedUser.memberName ||
    nestedUser.customerName ||
    nestedUser.name ||
    fallback.name ||
    fallback.username ||
    'Member';

  return {
    token: root.token || payload?.token,
    customerId: root.customerId || nestedUser.customerId || fallback.customerId || '',
    agentName: name,
    tenant: root.tenant || payload?.tenant || null,
    mobileNumber: root.mobileNumber || nestedUser.mobileNumber || fallback.mobileNumber || '',
    memberGl: root.memberGl || root.glId || nestedUser.memberGl || nestedUser.glId || fallback.memberGl || '',
  };
};

export const Login = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const { t, language, tenantBrand } = useLocalization();
  const defaultAuthenticatedPath = getDefaultAuthenticatedPath();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('password');
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  const [otpResendCount, setOtpResendCount] = useState(0);
  const [showVirtualKeyboard, setShowVirtualKeyboard] = useState(false);
  const [activeInput, setActiveInput] = useState(null);
  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const mobileInputRef = useRef(null);
  const otpInputRef = useRef(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    mobileNumber: '',
    otp: '',
  });
  const maskedMobileNumber = maskMobileNumber(formData.mobileNumber);

  useEffect(() => {
    if (isAuthenticated()) {
      navigate(defaultAuthenticatedPath, { replace: true });
    }
  }, [defaultAuthenticatedPath, navigate]);

  useEffect(() => {
    if (!otpSent || otpResendCountdown <= 0) return undefined;
    const timerId = window.setInterval(() => {
      setOtpResendCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [otpSent, otpResendCountdown]);

  const handleVirtualKeyboardInput = useCallback((value) => {
    if (!activeInput) return;
    
    const inputName = activeInput;
    
    if (value === 'BACKSPACE') {
      setFormData(prev => ({
        ...prev,
        [inputName]: prev[inputName].slice(0, -1)
      }));
    } else if (value === 'ENTER') {
      // Handle enter key based on current context
      if (loginMode === 'password') {
        handlePasswordLogin(new Event('submit'));
      } else if (loginMode === 'otp' && otpSent) {
        handleOTPLogin(new Event('submit'));
      } else if (loginMode === 'otp' && !otpSent) {
        handleRequestOTP(new Event('submit'));
      }
    } else if (value === 'CLEAR') {
      setFormData(prev => ({
        ...prev,
        [inputName]: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [inputName]: prev[inputName] + value
      }));
    }
  }, [activeInput, loginMode, otpSent]);

  const handleInputFocus = useCallback((inputName) => {
    setActiveInput(inputName);
  }, []);

  const handleVirtualKeyboardOpen = useCallback((inputName, inputRef) => {
    setActiveInput(inputName);
    setShowVirtualKeyboard(true);
    inputRef?.current?.focus();
  }, []);

  const handleVirtualKeyboardClose = useCallback(() => {
    setShowVirtualKeyboard(false);
    setActiveInput(null);
  }, []);

  const getActiveInputElement = useCallback(() => {
    switch (activeInput) {
      case 'username': return usernameInputRef.current;
      case 'password': return passwordInputRef.current;
      case 'mobileNumber': return mobileInputRef.current;
      case 'otp': return otpInputRef.current;
      default: return null;
    }
  }, [activeInput]);

  const resetOtpFlow = () => {
    setOtpSent(false);
    setOtpResendCountdown(0);
    setOtpResendCount(0);
    setFormData((prev) => ({ ...prev, otp: '' }));
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    const username = sanitizeUsernameInput(formData.username);
    const password = sanitizePasswordInput(formData.password);

    if (!username || !password.trim()) {
      toast.error(t('login.pleaseEnterUsernamePassword'));
      return;
    }
    setLoading(true);

    try {
      const response = await authAPI.login({
        username,
        password,
      });

      const { token, customerId, agentName, tenant, mobileNumber, memberGl } = normalizeAuthPayload(
        response.data,
        {
          username,
        }
      );
      if (!token) {
        throw new Error(t('login.tokenMissing'));
      }

      const user = {
        customerId,
        name: agentName,
        username,
        mobileNumber,
        memberGl,
      };

      setAuth(token, user, tenant);
      markPostLoginFeaturePromptPending();
      toast.success(t('login.welcomeMessage'));
      navigate(defaultAuthenticatedPath);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.loginFailed')));
    } finally {
      setLoading(false);
    }
  };

  const sendOtp = async ({ isResend = false } = {}) => {
    const mobileNumber = sanitizeIndianMobileInput(formData.mobileNumber);

    if (!INDIA_MOBILE_PATTERN.test(mobileNumber)) {
      toast.error(t('login.enterValidMobile'));
      return;
    }

    setLoading(true);
    try {
      await authAPI.requestOTP(mobileNumber);
      setOtpSent(true);
      setOtpResendCountdown(OTP_RESEND_COOLDOWN_SECONDS);
      setFormData((prev) => ({ ...prev, mobileNumber }));
      if (isResend) {
        setOtpResendCount((prev) => prev + 1);
        setFormData((prev) => ({ ...prev, otp: '' }));
        toast.success(t('login.otpResent'));
      } else {
        setOtpResendCount(0);
        toast.success(t('login.otpSent'));
      }
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.otpSendFailed')));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOTP = async (e) => {
    e?.preventDefault();
    await sendOtp({ isResend: false });
  };

  const handleResendOTP = async () => {
    if (otpResendCountdown > 0 || otpResendCount >= OTP_RESEND_MAX_ATTEMPTS) {
      return;
    }
    await sendOtp({ isResend: true });
  };

  const handleOTPLogin = async (e) => {
    e.preventDefault();
    if (!otpSent) {
      await handleRequestOTP(e);
      return;
    }
    const mobileNumber = sanitizeIndianMobileInput(formData.mobileNumber);
    const otp = sanitizeOtpInput(formData.otp);

    if (otp.length !== OTP_MAX_LENGTH) {
      toast.error(t('login.enterValidOtp'));
      return;
    }
    setLoading(true);

    try {
      const response = await authAPI.verifyOTP({
        mobileNumber,
        otp,
        username: '',
      });

      const { token, customerId, agentName, tenant, mobileNumber: resolvedMobileNumber, memberGl } =
        normalizeAuthPayload(response.data, {
          mobileNumber,
        });
      if (!token) {
        throw new Error(t('login.tokenMissing'));
      }

      const user = {
        customerId,
        name: agentName,
        mobileNumber: resolvedMobileNumber || mobileNumber,
        memberGl,
      };

      setAuth(token, user, tenant);
      markPostLoginFeaturePromptPending();
      toast.success(t('login.welcomeMessage'));
      navigate(defaultAuthenticatedPath);
    } catch (error) {
      toast.error(resolveApiErrorMessage(error, t, t('login.otpFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <SecurityManager enabled={true}>
      <div className="login-page-shell h-[100dvh] max-h-[100dvh] overflow-hidden grid md:grid-cols-2 app-page-shell-bg transition-colors relative">

        {/* LEFT HALF - BANNERS */}
        <div className="hidden md:flex relative flex-col h-full min-h-0 bg-gradient-to-br from-primary-200/70 via-primary-50/80 to-primary-50/90 dark:from-slate-900 dark:to-slate-950">
          <ImageCarousel 
            imageFolder="/banners/"
            autoPlay={true}
            interval={5000}
            showIndicators={true}
            showArrows={false}
            className="w-full h-full"
          />
        </div>

      {/* RIGHT HALF - LOGIN FORM & INFO */}
      <div className="flex flex-col h-full min-h-0">
        {/* TOP - CONTROLS */}
        <div className="flex-shrink-0 pt-2 pb-1 px-4 sm:px-6 md:px-8 flex justify-end">
          <div className="flex gap-2">
            <LanguageSwitcher compact />
            <ThemeToggle compact />
          </div>
        </div>

        {/* CENTER - LOGIN FORM */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 sm:px-8 md:px-8 py-1">
          <div className="w-full max-w-md mx-auto min-h-full flex flex-col justify-center space-y-3 sm:space-y-4 slide-up">
            {/* LOGO, TENANT NAME & APP NAME */}
            <div className="text-center space-y-2 sm:space-y-3">
              {/* Logo - Client logo with fallback */}
              <div className="flex justify-center">
                <img 
                  src={`${process.env.PUBLIC_URL}/logo192.png`}
                  alt={tenantBrand.fullName}
                  className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain drop-shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = '<div class="bg-primary-500 p-5 rounded-3xl shadow-xl"><svg class="w-14 h-14 sm:w-16 sm:h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path></svg></div>';
                  }}
                />
              </div>

              {/* Tenant Name - Full width, no line breaks forced */}
              <p className="text-primary-800 dark:text-primary-200 font-bold text-base sm:text-lg leading-snug px-2">
                {tenantBrand.fullName}
              </p>

              {/* App Name */}
              <p className="text-primary-600 dark:text-primary-400 font-semibold text-base sm:text-lg tracking-wide">
                {t('login.selfServicePortal')}
              </p>
            </div>

            <div className="rounded-[1.75rem] sm:rounded-[2rem] border border-primary-100/90 bg-white px-4 py-3 sm:px-5 sm:py-4 shadow-[0_24px_60px_-28px_rgba(240,99,33,0.28)] dark:border-slate-800 dark:bg-slate-900">
              {/* LOGIN TABS */}
              <div className="flex gap-2 bg-primary-50 dark:bg-slate-800 p-1 rounded-xl">
                <button
                  onClick={() => {
                    setLoginMode('password');
                    resetOtpFlow();
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                    loginMode === 'password'
                      ? 'bg-white dark:bg-slate-900 text-primary-900 dark:text-white shadow-sm'
                      : 'text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white'
                  }`}
                  data-testid="password-login-tab"
                >
                  {t('login.password')}
                </button>
                <button
                  onClick={() => {
                    setLoginMode('otp');
                    resetOtpFlow();
                    setFormData((prev) => ({ ...prev, password: '' }));
                  }}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    loginMode === 'otp'
                      ? 'bg-white dark:bg-slate-900 text-primary-900 dark:text-white shadow-sm'
                      : 'text-primary-700 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white'
                  }`}
                  data-testid="otp-login-tab"
                >
                  <Smartphone size={16} />
                  {t('login.otp')}
                </button>
              </div>

              {/* LOGIN FORMS */}
              {loginMode === 'password' ? (
              <form onSubmit={handlePasswordLogin} className="space-y-4 pt-4">
                <div className="space-y-4" data-testid="login-form">
                  <div className="space-y-1.5">
                    <label htmlFor="username" className="block text-sm font-medium text-primary-900 dark:text-white">
                      {t('login.usernameOrCustomerId')}
                    </label>
                    <div className="relative">
                      <input
                        ref={usernameInputRef}
                        id="username"
                        data-testid="username-input"
                        type="text"
                        required
                        maxLength={USERNAME_MAX_LENGTH}
                        value={formData.username}
                        onChange={(e) => setFormData({ ...formData, username: sanitizeUsernameInput(e.target.value) })}
                        onFocus={() => handleInputFocus('username')}
                        className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none transition-all"
                        placeholder={t('login.enterUsername')}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        readOnly={showVirtualKeyboard && activeInput === 'username'}
                      />
                      <button
                        type="button"
                        onClick={() => handleVirtualKeyboardOpen('username', usernameInputRef)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                        aria-label="Toggle Virtual Keyboard"
                      >
                        <Keyboard size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="password" className="block text-sm font-medium text-primary-900 dark:text-white">
                      {t('login.password')}
                    </label>
                    <div className="relative">
                      <input
                        ref={passwordInputRef}
                        id="password"
                        data-testid="password-input"
                        type={showPassword ? 'text' : 'password'}
                        required
                        maxLength={PASSWORD_MAX_LENGTH}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: sanitizePasswordInput(e.target.value) })}
                        onFocus={() => handleInputFocus('password')}
                        className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-24 outline-none transition-all"
                        placeholder={t('login.enterPassword')}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck={false}
                        readOnly={showVirtualKeyboard && activeInput === 'password'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-12 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                        data-testid="toggle-password-visibility"
                        aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleVirtualKeyboardOpen('password', passwordInputRef)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                        aria-label="Toggle Virtual Keyboard"
                      >
                        <Keyboard size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="app-primary-button w-full"
                >
                  {loading ? t('login.signingIn') : t('login.signIn')}
                </button>
              </form>
            ) : (
              <form onSubmit={handleOTPLogin} className="space-y-4 pt-4">
                <div className="space-y-1.5">
                  <label htmlFor="mobileNumber" className="block text-sm font-medium text-primary-900 dark:text-white">
                    {t('login.mobileNumber')}
                  </label>
                  <div className="relative">
                      <input
                        ref={mobileInputRef}
                        id="mobileNumber"
                        data-testid="mobile-input"
                        type="tel"
                        required
                        maxLength={10}
                        value={formData.mobileNumber}
                        onChange={(e) =>
                          setFormData({ ...formData, mobileNumber: sanitizeIndianMobileInput(e.target.value) })
                        }
                        onFocus={() => handleInputFocus('mobileNumber')}
                        className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none transition-all"
                        placeholder={t('login.enterMobileNumber')}
                        disabled={otpSent}
                        inputMode="numeric"
                        readOnly={showVirtualKeyboard && activeInput === 'mobileNumber'}
                      />
                      <button
                        type="button"
                        onClick={() => handleVirtualKeyboardOpen('mobileNumber', mobileInputRef)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                        aria-label="Toggle Virtual Keyboard"
                        disabled={otpSent}
                      >
                        <Keyboard size={20} />
                      </button>
                    </div>
                  {!otpSent && (
                    <p className="flex items-start gap-1.5 text-xs sm:text-sm text-primary-600 dark:text-primary-300 leading-relaxed">
                      <span aria-hidden="true" className="text-sm leading-none mt-0.5">🇮🇳</span>
                      <span>{t('login.mobileNumberHint')}</span>
                    </p>
                  )}
                </div>

                {!otpSent ? (
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="request-otp-button"
                    className="app-primary-button w-full"
                  >
                    {loading ? t('login.sendingOtp') : t('login.sendOtp')}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <label htmlFor="otp" className="block text-sm font-medium text-primary-900 dark:text-white">
                        {t('login.enterOtp')}
                      </label>
                      <p className="text-xs sm:text-sm text-primary-600 dark:text-primary-300">
                        {t('login.otpSentTo', { mobileNumber: maskedMobileNumber })}
                      </p>
                      <div className="relative">
                        <input
                          ref={otpInputRef}
                          id="otp"
                          data-testid="otp-input"
                          type="text"
                          required
                          maxLength={OTP_MAX_LENGTH}
                          value={formData.otp}
                          onChange={(e) => setFormData({ ...formData, otp: sanitizeOtpInput(e.target.value) })}
                          onFocus={() => handleInputFocus('otp')}
                          className="w-full bg-white dark:bg-slate-900 border border-primary-200 dark:border-slate-700 text-primary-900 dark:text-white placeholder-primary-300 dark:placeholder-primary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 rounded-lg h-12 px-4 pr-12 outline-none text-center text-2xl tracking-widest transition-all"
                          placeholder="000000"
                          inputMode="numeric"
                          readOnly={showVirtualKeyboard && activeInput === 'otp'}
                        />
                        <button
                          type="button"
                          onClick={() => handleVirtualKeyboardOpen('otp', otpInputRef)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-primary-600 dark:text-primary-300 hover:text-primary-900 dark:hover:text-white transition-colors"
                          aria-label="Toggle Virtual Keyboard"
                        >
                          <Keyboard size={20} />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        type="submit"
                        disabled={loading}
                        data-testid="verify-otp-button"
                        className="app-primary-button w-full"
                      >
                        {loading ? t('login.verifyingOtp') : t('login.verifyAndLogin')}
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOTP}
                        disabled={loading || otpResendCountdown > 0 || otpResendCount >= OTP_RESEND_MAX_ATTEMPTS}
                        className="w-full sm:w-auto whitespace-nowrap rounded-full border border-primary-200 px-5 py-3 text-sm font-medium text-primary-600 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:border-primary-100 disabled:text-primary-300 dark:border-primary-500/40 dark:text-primary-300 dark:hover:bg-primary-900/20 dark:disabled:border-slate-700 dark:disabled:text-slate-500"
                        data-testid="resend-otp-button"
                      >
                        {otpResendCountdown > 0
                          ? t('login.resendOtpIn', { seconds: otpResendCountdown })
                          : t('login.resendOtp')}
                      </button>
                    </div>

                    <div className="flex flex-col items-start gap-2 text-sm">
                      <button
                        type="button"
                        onClick={resetOtpFlow}
                        className="text-primary-500 hover:text-primary-600 hover:underline"
                        data-testid="change-number-button"
                      >
                        {t('login.changeMobileNumber')}
                      </button>
                      {otpResendCount >= OTP_RESEND_MAX_ATTEMPTS && (
                        <p className="text-primary-600 dark:text-primary-300">
                          {t('login.resendOtpLimitReached')}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </form>
            )}
            </div>
          </div>
        </div>

        {/* BOTTOM - FOOTER */}
        <div className="flex-shrink-0 border-t border-primary-200 dark:border-slate-800 px-5 sm:px-8 md:px-8 py-3 sm:py-4 space-y-1">
          {/* <p className="text-center text-sm text-primary-600 dark:text-primary-300 leading-relaxed">
            {t('login.secureServices')}
          </p> */}
          <p className="text-center text-xs text-primary-600 dark:text-primary-300">
            © {currentYear} {t('login.copyright')}
          </p>
          <p className="text-center text-xs text-primary-600 dark:text-primary-300">
            {t('login.poweredBy', {}, 'Powered by')}{' '}
            <a
              href="https://www.ykinnosoft.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-primary-600 underline decoration-primary-300 underline-offset-4 transition-colors hover:text-primary-900 hover:decoration-primary-500 dark:text-primary-300 dark:decoration-primary-500/80 dark:hover:text-white dark:hover:decoration-white"
            >
              {t('layout.companyName', {}, 'YK Innosoft Technologies')}
            </a>
          </p>
        </div>
      </div>

      {/* Virtual Keyboard */}
      {showVirtualKeyboard && (
        <VirtualKeyboard
          onInput={handleVirtualKeyboardInput}
          onClose={handleVirtualKeyboardClose}
          targetInput={getActiveInputElement()}
          showNumbers={true}
          showSymbols={true}
        />
      )}
    </div>
    </SecurityManager>
  );
};
